/**
 * 출정 편성을 정하는 순수 규칙. 화면(`ui/sortie.ts`)이 쓰고, 시험이 DOM 없이 돌린다.
 *
 * 출정 화면이 열리자마자 자동 편성을 채우게 되면서 이 규칙이 첫 화면의 상태가 됐다.
 * 화면 안에 두면 고칠 때마다 조용히 깨질 수 있어 따로 뗐다(리뷰가 권했다).
 */
import { MAX_SQUADS } from '../sim/commands.ts';
import { COMPANIONS, TROOP_BY_ID, troopPower } from '../sim/troops.ts';
import { SQUAD_SIZE, availableCompanions, mateSquadSize, soldierLevel, squadSize } from './army.ts';
import type { Army } from './army.ts';

const COMPANION_BY_ID = new Map(COMPANIONS.map((c) => [c.id, c]));

export interface Plan {
  /** 분대별 병사 uid. 0번은 장수 직할대다. */
  readonly squads: number[][];
  /** 분대별 동료 id. 0번은 늘 null(장수 자리). */
  readonly companions: (string | null)[];
  /**
   * 장수 곁(내 부대)에서 싸우는 동료(부관). 한 명까지. **부하 부대의 자리를 차지하지 않는다**(`companions`에 없다) —
   * 출정할 때 빈 부대 자리 하나에 실린다(`startRun`, 출정 명부가 분대별 동료라). 동료의 지휘 보정은 내 부대에 더해진다
   * (`ScenarioConfig.escortSquad`). 플레이한 사람이 청했다: "부하 장수도 내 부대에 편성할 수 있게 해줘."
   *
   * 한때 제 분대 자리를 지켜 그 분대가 "온달 부대 · 닫힘"으로 남았다. "부하 부대의 장수를 고정하지 말아줘. 온달을
   * 주인공 부대에 넣었으면 온달 부대라는 개념은 이제 없는거야"라고 해 자리를 풀었다. 부대 이름도 "제2부대"처럼 번호다.
   */
  escort?: string | null;
}

/**
 * 분대 정원을 재는 법. 내 부대는 장수의 관등(`hero`), 부하 부대는 맡은 동료의 관등(`mate`, `mateSquadSize`)이다. 예전에는
 * 모든 분대가 장수의 정원 하나를 나눠 썼다. 숫자 하나를 받는 곳(`size`)은 모든 분대가 그 정원이다 — 시험·옛 호출용.
 */
export interface Capacity {
  readonly hero: number;
  readonly mate: (id: string) => number;
}

export function capacityOf(army: Army): Capacity {
  return { hero: squadSize(army), mate: (id) => mateSquadSize(army, id) };
}

/** 분대 `sq`의 정원. 지휘관 없는 부하 부대는 장수의 정원으로 친다(병사를 받지 않으므로 뜻이 없다). */
export function capOf(size: number | Capacity, plan: Plan, sq: number): number {
  if (typeof size === 'number') return size;
  const c = sq > 0 ? plan.companions[sq] : null;
  return c ? size.mate(c) : size.hero;
}

function heroCap(size: number | Capacity): number {
  return typeof size === 'number' ? size : size.hero;
}

function empty(): Plan {
  const squads: number[][] = [];
  const companions: (string | null)[] = [];
  for (let i = 0; i < MAX_SQUADS; i++) {
    squads.push([]);
    companions.push(null);
  }
  return { squads, companions, escort: null };
}

/** 지휘관이 없는 부하 부대 자리 수. 부관은 출정할 때 이 가운데 하나에 실린다(`startRun`). */
export function freeSeats(plan: Plan): number {
  let n = 0;
  for (let sq = 1; sq < plan.companions.length; sq++) if (plan.companions[sq] === null) n++;
  return n;
}

/**
 * 병사를 넣을 수 있는 분대. 0번(직할대)은 늘 열려 있고, 부하 부대는 맡길 동료가
 * 있을 때만 열린다. `ready`는 나설 수 있는 동료, 분대 1부터 순서대로 맡는다.
 */
export function openSquads(readyCount: number): number[] {
  const open = [0];
  for (let sq = 1; sq < MAX_SQUADS && sq <= readyCount; sq++) open.push(sq);
  return open;
}

/**
 * 분대마다 정원(관등에 따라 `squadSize`)까지 알아서 채운다. 강한 병사부터, 부대는 가지별로 나눈다.
 *
 * 병종은 그 병종을 잘 부리는 부하장수의 부대로 간다(아래 '병종이 갈 부대' — 예전에는 누가 맡든 창은 첫째, 궁은 둘째,
 * 기병은 셋째 부하 부대였다). 갈 부대가 없거나 찼으면 자리가 남은 부대로 간다(직할대부터). 병사가 가지 않은 부대에는
 * 부하장수를 두지 않는다. 요양 중인 부하장수는 맡기지 않는다.
 *
 * 예전에는 지휘력(전체 인원) 하나만 봤다. 분대 정원이 생기면서 "창병 스물을 창 부대에"
 * 같은 배치가 넘치게 됐다 — 넘친 병사는 다른 부대로 흘려야 한다.
 *
 * **잠근 병사·동료(`locked`)는 옮기지 않는다.** 지금 편성(`cur`)을 주면 잠근 병사는 앉아 있던 부대에 먼저 앉고,
 * 병영에 있던 잠근 병사는 병영에 남는다. 나머지를 위 규칙으로 채운다.
 * - 잠근 병사가 앉은 부하 부대는 **그 부대의 동료도 그대로** 둔다 — 동료가 바뀌면 정원도 기술도 바뀌어 "그 자리에
 *   그대로"가 아니다. 나머지 동료는 남은 부대를 앞에서부터 맡는다.
 * - 잠근 동료는 맡은 부대에 남는다 — 병사가 하나도 가지 않아도(혼자 나선다). 부관(장수 곁)으로 잠갔으면 부관으로
 *   남고, 그 동료가 실릴 부하 부대 자리 하나를 비워 둔다(`moveCompanion`의 `noSeat`와 같은 규칙). 병영에서 잠갔으면
 *   부대를 맡기지 않는다. 잠그지 않은 부관은 예전처럼 부대를 맡으러 간다.
 * - 그 부대를 맡은 동료가 지금 나설 수 없으면(요양·수비) 부대가 닫혀 자리를 지킬 수 없다. 그 병사는 잠금과 무관하게
 *   다른 병사처럼 다시 앉힌다(잠금은 풀지 않는다). 정원을 넘는 잠근 병사도 같다.
 * - `cur`가 없으면(처음 여는 화면, 측정 도구) 잠금을 보지 않는다 — 지킬 자리가 없다.
 */
export function autoPlan(army: Army, cur?: Plan): Plan {
  const plan = empty();
  const ready = availableCompanions(army);
  const cap = capacityOf(army);
  const readyIds = new Set(ready.map((c) => c.id));
  const mateAt: (string | null)[] = plan.companions.map(() => null);
  // 잠근 동료의 자리: 맡은 부대, 부관, 병영. 나설 수 없는 동료는 `ready`에 없어 어차피 앉지 못한다.
  const keepSeat = new Set<number>();
  const idle = new Set<string>();
  if (cur) {
    for (const c of army.companions) {
      if (c.locked !== true || !readyIds.has(c.id)) continue;
      const sq = cur.companions.indexOf(c.id);
      if (sq > 0) {
        mateAt[sq] = c.id;
        keepSeat.add(sq);
      } else if (cur.escort === c.id) plan.escort = c.id;
      else idle.add(c.id);
    }
  }
  // 잠근 병사의 자리. 부대를 맡은 동료가 나설 수 있을 때만 지킨다.
  const stay = new Map<number, number | 'barracks'>();
  if (cur) {
    for (const s of army.soldiers) {
      if (s.locked !== true) continue;
      const at = placeOf(cur, s.uid);
      if (at === 'barracks') {
        stay.set(s.uid, 'barracks');
        continue;
      }
      const mate = at > 0 ? cur.companions[at] : null;
      if (at > 0 && (mate === null || !readyIds.has(mate))) continue;
      stay.set(s.uid, at);
      // 부대를 맡을 동료: 잠근 병사가 앉은 부대는 지금 동료 그대로.
      if (at > 0) mateAt[at] = mate;
    }
  }
  // 나머지 부대는 남은 동료가 앞에서부터. 잠근 부관이 있으면 그가 실릴 자리 하나는 비워 둔다.
  const rest = ready.filter((c) => !mateAt.includes(c.id) && c.id !== plan.escort && !idle.has(c.id));
  const seats = MAX_SQUADS - 1 - (plan.escort ? 1 : 0);
  let taken = mateAt.filter((m) => m !== null).length;
  for (let sq = 1, k = 0; sq < MAX_SQUADS && k < rest.length && taken < seats; sq++) {
    if (mateAt[sq] === null) {
      mateAt[sq] = rest[k++].id;
      taken++;
    }
  }
  const open = [0];
  for (let sq = 1; sq < MAX_SQUADS; sq++) if (mateAt[sq] !== null) open.push(sq);
  const sizeAt = (sq: number): number => (sq === 0 ? cap.hero : cap.mate(mateAt[sq]!));
  // 잠근 병사를 먼저 앉힌다 — 지금 편성의 순서 그대로(줄 선 자리도 지킨다). 정원을 넘으면 뒤에서부터 풀려난다.
  if (cur) {
    for (const sq of open) {
      for (const uid of cur.squads[sq] ?? []) {
        if (stay.get(uid) !== sq) continue;
        if (plan.squads[sq].length < sizeAt(sq)) plan.squads[sq].push(uid);
        else stay.delete(uid);
      }
    }
  }
  const sorted = army.soldiers.filter((s) => !stay.has(s.uid)).sort((a, b) => {
    const ta = TROOP_BY_ID.get(a.troop)!;
    const tb = TROOP_BY_ID.get(b.troop)!;
    return (
      tb.tier - ta.tier ||
      soldierLevel(b) - soldierLevel(a) ||
      troopPower(tb) - troopPower(ta) ||
      a.uid - b.uid
    );
  });
  // **병종이 갈 부대**(Todo11). 플레이한 사람: "자동 편성도 부하장수 특성을 고려해서 배치하게 해주고." 예전에는 누가
  // 맡든 창은 1번, 궁은 2번, 기병은 3번이었다.
  // 1. 부하장수가 잘 부리는 병종(`MateTrait.favor`)은 그 사람의 부대로 — 그 병종의 병사가 있을 때만(없는 병종을 기다리며
  //    부대를 비워 두지 않는다). 같은 병종을 둘이 잘 부리면 앞 부대가 갖는다.
  // 2. 내 부대의 선호 병종(`Army.heroBranch`)은 내 부대로. 부하장수가 이미 가져간 병종이면 그 부대가 찬 뒤의 몫이 온다.
  // 3. 아직 병종을 받지 못한 부하 부대는 남은 병종을 예전 순서(창·궁·기병)로 하나씩 받는다 — 잘 부리는 병종의 병사가
  //    없는 초반에도 부대가 갈린다(지휘 보정은 병종을 가리지 않는다).
  // 4. 나머지(도검·민병, 받을 부대가 없는 병종)는 내 부대부터 빈자리로.
  const present = new Set<string>(sorted.map((s) => TROOP_BY_ID.get(s.troop)!.branch));
  const home = new Map<string, number>();
  for (const sq of open) {
    if (sq === 0) continue;
    const favor = COMPANION_BY_ID.get(mateAt[sq]!)?.trait.favor;
    if (favor !== undefined && present.has(favor) && !home.has(favor)) home.set(favor, sq);
  }
  const hb = army.heroBranch;
  if (hb !== undefined && !home.has(hb)) home.set(hb, 0);
  const claimed = new Set(home.values());
  const spare = open.filter((sq) => sq > 0 && !claimed.has(sq));
  for (const b of ['spear', 'bow', 'horse']) {
    if (spare.length === 0) break;
    if (present.has(b) && !home.has(b)) home.set(b, spare.shift()!);
  }
  const room = (sq: number): boolean => open.includes(sq) && plan.squads[sq].length < sizeAt(sq);
  // 갈 부대가 있는 병종을 먼저 앉힌다 — 뒤에 두면 갈 곳 없는 병사가 그 부대의 자리를 먼저 채운다.
  const homed = sorted.filter((s) => home.has(TROOP_BY_ID.get(s.troop)!.branch) || TROOP_BY_ID.get(s.troop)!.branch === hb);
  const others = sorted.filter((s) => !homed.includes(s));
  for (const s of [...homed, ...others]) {
    const branch = TROOP_BY_ID.get(s.troop)!.branch;
    const want = home.get(branch) ?? 0;
    const target = room(want) ? want : branch === hb && room(0) ? 0 : open.find((sq) => room(sq));
    // 자리가 다 찼다 — 남은 병사는 병영에 남는다.
    if (target === undefined) break;
    plan.squads[target].push(s.uid);
  }
  for (let sq = 1; sq < MAX_SQUADS; sq++) {
    // 병사가 가지 않은 부대에는 동료를 두지 않는다 — 잠근 동료만 빼고(혼자 나선다).
    plan.companions[sq] = plan.squads[sq].length > 0 || keepSeat.has(sq) ? mateAt[sq] : null;
  }
  return plan;
}

/**
 * 병영을 열린 분대에 고르게 나눈다(시험 모드의 곧장 출정). 분대에 고르게 나눠야 분대
 * 명령이 의미를 갖는다. 맡길 동료가 있는 부대에만 나눈다 — 동료 없는 부하 부대는
 * 열리지 않는다.
 */
export function evenPlan(army: Army): Plan {
  const plan = empty();
  const ready = availableCompanions(army);
  const open = openSquads(ready.length);
  const cap = capacityOf(army);
  const sizeAt = (sq: number): number => (sq === 0 ? cap.hero : cap.mate(ready[sq - 1].id));
  const pool = [...army.soldiers].sort((a, b) => b.xp - a.xp || a.uid - b.uid);
  // 돌아가며 한 명씩, 찬 분대는 건너뛴다(분대마다 정원이 다르다).
  let k = 0;
  for (const s of pool) {
    let placed = false;
    for (let tries = 0; tries < open.length && !placed; tries++) {
      const sq = open[k % open.length];
      k++;
      if (plan.squads[sq].length < sizeAt(sq)) {
        plan.squads[sq].push(s.uid);
        placed = true;
      }
    }
    if (!placed) break;
  }
  for (let sq = 1; sq < MAX_SQUADS; sq++) {
    plan.companions[sq] = plan.squads[sq].length > 0 ? (ready[sq - 1]?.id ?? null) : null;
  }
  return plan;
}
/**
 * 새로 뽑은 병사를 열린 분대의 빈자리에 앞 분대부터 앉힌다. 이미 짠 편성은 건드리지 않는다.
 * 자리가 없으면 병영에 남는다. 돌려주는 것은 앉힌 수다.
 *
 * "부족분 고용 뒤 자동 편성까지"를 청받았다. `autoPlan`을 다시 돌리면 손으로 짠 편성이 통째로
 * 갈려 나가므로, 새 사람만 빈자리에 넣는다.
 */
export function seatNew(
  squads: number[][],
  open: readonly boolean[],
  uids: readonly number[],
  size: number | readonly number[] = SQUAD_SIZE,
): number {
  // 분대마다 정원이 다르면 배열로 받는다(`capOf`).
  const cap = (i: number): number => (typeof size === 'number' ? size : (size[i] ?? SQUAD_SIZE));
  let seated = 0;
  for (const uid of uids) {
    if (squads.some((s) => s.includes(uid))) continue;
    const sq = squads.findIndex((s, i) => open[i] && s.length < cap(i));
    if (sq < 0) break;
    squads[sq].push(uid);
    seated++;
  }
  return seated;
}

/**
 * 지휘관을 거둔 분대를 닫는다. 병사는 내 부대(0번)의 빈자리까지만 옮기고 나머지는 병영으로 돌아간다.
 * 돌려주는 것은 병영으로 돌아간 수다. 처음에는 전부 내 부대로 옮겨 열두 명 정원이 스물넷이 됐다.
 */
export function closeSquad(squads: number[][], sq: number, size = SQUAD_SIZE): number {
  const room = Math.max(0, size - squads[0].length);
  const moved = squads[sq].slice(0, room);
  const back = squads[sq].length - moved.length;
  squads[0].push(...moved);
  squads[sq] = [];
  return back;
}

/**
 * 저장해 둔 편성(`Army.formation`)을 지금 계정에 맞춰 되살린다. 없거나 모양이 틀렸으면 null(자동 편성).
 *
 * - 없어진 병사(전사·은퇴)와 겹친 uid는 뺀다.
 * - 동료는 지금 나설 수 있을(요양 중이 아닐) 때만 앉힌다. 앉았던 자리 번호는 그대로다 — 1번 동료가 요양에
 *   들어가도 2번 동료와 그 분대는 2번에 남는다. 처음에는 "분대 번호 ≤ 나설 수 있는 동료 수"도 따져, 멀쩡한
 *   2번 동료까지 병영으로 밀리고 분대가 흩어졌다(리뷰가 짚었다). 못 앉은 동료의 분대는 닫힌다 — 병사는
 *   내 부대 빈자리까지 옮기고 나머지는 병영(`closeSquad`와 같은 규칙).
 * - 정원(관등)을 넘는 병사는 병영으로.
 * - 새로 합류한 동료는 어느 분대에도 없으므로 병영에 선다. 짜 둔 편성은 그대로다.
 */
export function restorePlan(army: Army): Plan | null {
  const f = army.formation;
  if (!f || !Array.isArray(f.squads) || !Array.isArray(f.companions)) return null;
  const plan = empty();
  const cap = capacityOf(army);
  const ready = availableCompanions(army).map((c) => c.id);
  for (let sq = 1; sq < MAX_SQUADS; sq++) {
    const id = f.companions[sq];
    if (typeof id === 'string' && ready.includes(id) && !plan.companions.includes(id)) {
      plan.companions[sq] = id;
    }
  }
  // 부관은 나설 수 있을 때만 되살린다(요양에 들어갔으면 풀린다). 병사보다 먼저 — 예전 편성은 부관이 제 분대 자리도
  // 지켰다. 그 자리를 풀면 그 분대는 지휘관이 없으니 거기 적힌 병사는 닫힌 분대의 병사로 다룬다. 출정할 때 실을 빈
  // 자리가 없으면(망가진 저장) 부관을 풀어 병영에 둔다.
  const esc = (f as { escort?: unknown }).escort;
  if (typeof esc === 'string' && ready.includes(esc)) {
    const at = plan.companions.indexOf(esc);
    if (at > 0) plan.companions[at] = null;
    if (freeSeats(plan) > 0) plan.escort = esc;
  }
  const have = new Set(army.soldiers.map((s) => s.uid));
  const seen = new Set<number>();
  const orphans: number[] = [];
  for (let sq = 0; sq < MAX_SQUADS; sq++) {
    const list = Array.isArray(f.squads[sq]) ? f.squads[sq] : [];
    for (const uid of list) {
      if (!Number.isInteger(uid) || !have.has(uid) || seen.has(uid)) continue;
      seen.add(uid);
      if (sq > 0 && plan.companions[sq] === null) orphans.push(uid);
      // 정원은 그 분대 지휘관의 관등이다(`capOf`). 동료마다 정원이 나뉘기 전에 짠 편성은 넘친 만큼 병영으로 간다.
      else if (plan.squads[sq].length < capOf(cap, plan, sq)) plan.squads[sq].push(uid);
    }
  }
  // 닫힌 분대의 병사: 내 부대 빈자리까지, 나머지는 병영.
  for (const uid of orphans) if (plan.squads[0].length < cap.hero) plan.squads[0].push(uid);
  return plan;
}

/** 편성 화면의 한 칸이 띄우는 곳: 분대 번호, 또는 병영(어느 분대에도 없는 병사와 동료). */
export type Place = number | 'barracks';

/** 옮기지 못한 까닭. 화면이 알림 문구로 바꾼다. */
export type MoveRefusal = 'full' | 'noCommander' | 'leaderSquad' | 'occupied' | 'escortTaken' | 'noSeat';

export type MoveResult =
  | { readonly ok: true; /** 지휘관이 떠나 병영으로 돌아간 병사 수 */ readonly back: number }
  | { readonly ok: false; readonly why: MoveRefusal };

/** 병사가 선 곳. 어느 분대에도 없으면 병영이다. */
export function placeOf(plan: Plan, uid: number): Place {
  const sq = plan.squads.findIndex((list) => list.includes(uid));
  return sq < 0 ? 'barracks' : sq;
}

/**
 * 병사 한 명을 `to`로 옮긴다. 편성 화면에서 카드를 누르면 맞은편 칸으로 가는 규칙이다.
 *
 * 부하 부대는 지휘관(동료)이 있어야 병사를 받는다 — 지휘관 없는 부대는 열리지 않고
 * 출정하지도 않는다(`openSquads`). 정원(관등에 따라 `squadSize`)이 찼으면 옮기지 않는다. 다른 부대로
 * 몰래 보내면 고른 칸과 다른 곳에 병사가 가 있다.
 */
export function moveSoldier(plan: Plan, uid: number, to: Place, size: number | Capacity = SQUAD_SIZE): MoveResult {
  const from = placeOf(plan, uid);
  if (from === to) return { ok: true, back: 0 };
  if (to !== 'barracks') {
    if (to > 0 && plan.companions[to] === null) return { ok: false, why: 'noCommander' };
    if (plan.squads[to].length >= capOf(size, plan, to)) return { ok: false, why: 'full' };
  }
  if (from !== 'barracks') plan.squads[from] = plan.squads[from].filter((u) => u !== uid);
  if (to !== 'barracks') plan.squads[to].push(uid);
  return { ok: true, back: 0 };
}

/**
 * 동료를 `to`로 옮긴다. 분대로 가면 그 분대의 지휘관 자리에 앉고, 병영으로 가면 맡던 분대가 닫힌다.
 *
 * - 내 부대(0번)로 가면 부관이 된다(`Plan.escort`, 한 명까지). 맡던 부대는 자리를 풀고 닫는다(`closeSquad`): 휘하
 *   병사는 내 부대 빈자리까지 옮기고 나머지는 병영. 부관은 출정할 때 빈 부대 자리 하나에 실리므로(`startRun`), 부관이
 *   있는 동안에는 부하 부대 자리가 하나는 비어 있어야 한다 — 없으면 거절한다(`noSeat`).
 * - 이미 지휘관이 있는 분대에는 앉지 않는다. 바꾸려면 앉은 이를 먼저 병영으로 보낸다 — 몰래 맞바꾸면
 *   누른 카드와 다른 동료가 움직인다.
 * - 다른 분대로 옮겨 앉으면 **휘하 병사도 따라간다.** 지휘관만 옮기고 병사를 옛 분대에 두면 그 분대가
 *   닫히며 병사가 흩어진다.
 * - 병영으로 물러나면 맡던 분대를 닫는다(`closeSquad`): 병사는 내 부대 빈자리까지 옮기고 나머지는 병영.
 */
export function moveCompanion(plan: Plan, id: string, to: Place, size: number | Capacity = SQUAD_SIZE): MoveResult {
  const from = plan.companions.indexOf(id);
  // 닫히는 분대의 병사는 내 부대(장수의 정원)로 간다.
  const hero = heroCap(size);
  if (to === 0) {
    if (plan.escort === id) return { ok: true, back: 0 };
    if (plan.escort) return { ok: false, why: 'escortTaken' };
    if (from < 0 && freeSeats(plan) === 0) return { ok: false, why: 'noSeat' };
    plan.escort = id;
    if (from < 0) return { ok: true, back: 0 };
    plan.companions[from] = null;
    return { ok: true, back: closeSquad(plan.squads, from, hero) };
  }
  // 장수 곁을 떠난다 — 옮길 수 있을 때만. 거절 판정보다 먼저 풀었더니, 곁의 동료를 다른 지휘관이 앉은 분대로 끌면
  // "이미 지휘관이 있다"로 거절되면서도 곁에서는 빠졌다(리뷰가 짚었다).
  if (to !== 'barracks' && to !== from && plan.companions[to] !== null) return { ok: false, why: 'occupied' };
  // 병영에서 새로 앉으면 부관이 실릴 빈 자리가 남아야 한다.
  if (to !== 'barracks' && from < 0 && plan.escort && plan.escort !== id && freeSeats(plan) <= 1) {
    return { ok: false, why: 'noSeat' };
  }
  if (plan.escort === id) plan.escort = null;
  if (to === 'barracks') {
    if (from < 0) return { ok: true, back: 0 };
    plan.companions[from] = null;
    return { ok: true, back: closeSquad(plan.squads, from, hero) };
  }
  if (from === to) return { ok: true, back: 0 };
  plan.companions[to] = id;
  if (from > 0) {
    plan.companions[from] = null;
    plan.squads[to] = plan.squads[from];
    plan.squads[from] = [];
  }
  return { ok: true, back: 0 };
}

/**
 * 같은 분대 안에서 병사의 순서를 바꾼다: `uid`를 `target`이 선 자리로. 앞에서 뒤로 옮기면 target 뒤에, 뒤에서
 * 앞으로 옮기면 target 앞에 선다 — 끌어 놓은 카드의 자리를 차지한다. 둘이 같은 분대가 아니면 아무것도 안 한다.
 *
 * 편성 칸은 예전에 병종 순서로 늘 다시 줄을 세웠다. 플레이한 사람이 "자동 정렬은 끄고 순서는 드래그로 직접"을
 * 청했다. 줄은 분대 배열 순서 그대로 보이고, 이 순서가 저장된다.
 */
export function reorderSoldier(plan: Plan, uid: number, target: number): boolean {
  if (uid === target) return false;
  const sq = placeOf(plan, uid);
  if (sq === 'barracks' || placeOf(plan, target) !== sq) return false;
  const list = plan.squads[sq];
  const from = list.indexOf(uid);
  const to = list.indexOf(target);
  list.splice(from, 1);
  list.splice(to, 0, uid);
  return true;
}
